import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../context/ThemeContext';
import { activityAPI, bustCache } from '../utils/api';
import { formatChangeValue } from '../utils/html';
import { prettyFieldLabel } from '../utils/labels';

/* ─────────────────────────────────────────────────────────────────────────────
   GlobalHistoryPanel — full-module activity feed with filters, search,
   date grouping, and pagination. Now theme-aware.
   Props unchanged:
     module  {string}   — 'Candidate' | 'Requirement'
     onClose {function}
   ───────────────────────────────────────────────────────────────────────────── */

const ACTION_META = {
  CREATE_REQUIREMENT:  { label: 'Requirement Created', color: 'text-emerald-500 dark:text-emerald-400', dot: 'bg-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
  UPDATE_REQUIREMENT:  { label: 'Requirement Updated', color: 'text-indigo-500  dark:text-indigo-400',  dot: 'bg-indigo-400',  bg: 'bg-indigo-400/10 border-indigo-400/20'  },
  DELETE_REQUIREMENT:  { label: 'Requirement Deleted', color: 'text-red-500     dark:text-red-400',     dot: 'bg-red-400',     bg: 'bg-red-400/10 border-red-400/20'         },
  CREATE_CANDIDATE:    { label: 'Candidate Created',   color: 'text-emerald-500 dark:text-emerald-400', dot: 'bg-emerald-400', bg: 'bg-emerald-400/10 border-emerald-400/20' },
  UPDATE_CANDIDATE:    { label: 'Candidate Updated',   color: 'text-indigo-500  dark:text-indigo-400',  dot: 'bg-indigo-400',  bg: 'bg-indigo-400/10 border-indigo-400/20'  },
  DELETE_CANDIDATE:    { label: 'Candidate Deleted',   color: 'text-red-500     dark:text-red-400',     dot: 'bg-red-400',     bg: 'bg-red-400/10 border-red-400/20'         },
  STATUS_CHANGE:       { label: 'Status Changed',      color: 'text-amber-600   dark:text-amber-400',   dot: 'bg-amber-400',   bg: 'bg-amber-400/10 border-amber-400/20'     },
  UPDATE_STATUS:       { label: 'Status Changed',      color: 'text-amber-600   dark:text-amber-400',   dot: 'bg-amber-400',   bg: 'bg-amber-400/10 border-amber-400/20'     },
  UPLOAD_RESUME:       { label: 'Resume Uploaded',     color: 'text-cyan-600    dark:text-cyan-400',    dot: 'bg-cyan-400',    bg: 'bg-cyan-400/10 border-cyan-400/20'       },
  SEND_EMAIL:          { label: 'Email Sent',          color: 'text-sky-600     dark:text-sky-400',     dot: 'bg-sky-400',     bg: 'bg-sky-400/10 border-sky-400/20'         },
  ASSIGN_RECRUITER:    { label: 'Recruiter Assigned',  color: 'text-violet-500  dark:text-violet-400',  dot: 'bg-violet-400',  bg: 'bg-violet-400/10 border-violet-400/20'  },
};

function getMeta(action = '') {
  if (ACTION_META[action]) return ACTION_META[action];
  if (action.startsWith('CREATE')) return ACTION_META.CREATE_CANDIDATE;
  if (action.startsWith('UPDATE')) return ACTION_META.UPDATE_CANDIDATE;
  if (action.startsWith('DELETE')) return ACTION_META.DELETE_CANDIDATE;
  return {
    label: action.replace(/_/g, ' '),
    color: 'text-[var(--text-tertiary)]',
    dot: 'bg-slate-500',
    bg: 'bg-[var(--surface-3)] border-[var(--border-subtle)]',
  };
}

function relTime(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60)  return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)   return `${d}d ago`;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function absTime(dateStr) {
  return new Date(dateStr).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/* ─── Role badge ─────────────────────────────────────────────────────────── */
function RoleBadge({ role }) {
  const cls = {
    SuperAdmin:       'bg-amber-500/20 text-amber-700 dark:text-amber-300 border-amber-500/30',
    RecruiterManager: 'bg-violet-500/20 text-violet-700 dark:text-violet-300 border-violet-500/30',
    Recruiter:        'bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 border-indigo-500/30',
  }[role] || 'bg-[var(--surface-3)] text-[var(--text-tertiary)] border-[var(--border-subtle)]';
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${cls}`}>{role}</span>;
}

/* ─── Field change rows ──────────────────────────────────────────────────── */
function FieldChanges({ changes }) {
  const entries = Object.entries(changes);
  if (!entries.length) return null;
  // Renders any value readably — HTML→text, arrays/objects→summary, never
  // "[object Object]". See formatChangeValue.
  const fmt = (v) => {
    const text = formatChangeValue(v);
    return text || <span className="italic" style={{ color: 'var(--text-faint)' }}>—</span>;
  };
  return (
    <div
      className="mt-2 pt-2 space-y-0.5"
      style={{ borderTop: '1px solid var(--border-subtle)' }}
    >
      {entries.map(([field, val]) => (
        <div key={field} className="flex items-baseline gap-2 text-xs">
          <span
            className="min-w-[80px] shrink-0 capitalize"
            style={{ color: 'var(--text-muted)' }}
          >
            {prettyFieldLabel(field)}
          </span>
          {/* min-w-0 + break-words keep long values (RTE text, UUIDs) inside the panel. */}
          <span className="min-w-0 flex-1" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            <span className="text-red-500/85 dark:text-red-300/70 line-through">{fmt(val?.before ?? val?.old)}</span>
            <span style={{ color: 'var(--text-muted)' }}> → </span>
            <span className="text-emerald-600 dark:text-emerald-300/80">{fmt(val?.after ?? val?.new)}</span>
          </span>
        </div>
      ))}
    </div>
  );
}

/* ─── Single feed entry ──────────────────────────────────────────────────── */
function FeedEntry({ log }) {
  const [expanded, setExpanded] = useState(false);
  const meta       = getMeta(log.action);
  const changes    = log.details?.changes || {};
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="relative pl-8 group">
      {/* Dot — cut out by drawer bg */}
      <span
        className={`absolute left-0 top-3 w-2.5 h-2.5 rounded-full border-2 ${meta.dot} transition-transform group-hover:scale-125`}
        style={{ borderColor: 'var(--surface-2)' }}
      />

      <div className={`rounded-xl border px-3.5 py-3 mb-0.5 transition-all hover:brightness-110 ${meta.bg}`}>
        {/* Top row: action + time */}
        <div className="flex items-start justify-between gap-2">
          <span className={`font-semibold text-xs leading-tight ${meta.color}`}>{meta.label}</span>
          <span
            className="text-[11px] shrink-0 whitespace-nowrap"
            title={absTime(log.createdAt)}
            style={{ color: 'var(--text-muted)' }}
          >
            {relTime(log.createdAt)}
          </span>
        </div>

        {/* Entity name */}
        {log.targetName && (
          <p
            className="text-xs font-medium mt-0.5 break-words"
            style={{ color: 'var(--text-primary)', opacity: 0.9, overflowWrap: 'anywhere' }}
          >
            {log.targetName}
          </p>
        )}

        {/* Actor */}
        <div className="flex items-center gap-1.5 mt-1.5 min-w-0">
          <div className="w-4 h-4 rounded-full bg-indigo-700/60 flex items-center justify-center text-[9px] text-white font-bold shrink-0">
            {log.user?.name?.[0]?.toUpperCase() || '?'}
          </div>
          <span className="text-[11px] min-w-0 truncate" style={{ color: 'var(--text-tertiary)' }}>
            {log.user?.name || 'Unknown'}
          </span>
          {log.user?.role && <RoleBadge role={log.user.role} />}
        </div>

        {/* Notes */}
        {log.details?.note && (
          <p className="text-xs mt-1.5 italic" style={{ color: 'var(--text-tertiary)' }}>
            {log.details.note}
          </p>
        )}

        {/* Expandable changes */}
        {hasChanges && (
          <>
            <button
              onClick={() => setExpanded(v => !v)}
              className="mt-2 flex items-center gap-1 text-[11px] transition-colors"
              style={{ color: 'var(--text-muted)' }}
              onMouseEnter={e => (e.currentTarget.style.color = 'var(--text-secondary)')}
              onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
            >
              <span className="text-[9px]">{expanded ? '▼' : '▶'}</span>
              {Object.keys(changes).length} field{Object.keys(changes).length !== 1 ? 's' : ''} changed
            </button>
            {expanded && <FieldChanges changes={changes} />}
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Grouped by date ────────────────────────────────────────────────────── */
function groupByDate(logs) {
  const groups = [];
  let current = null;
  logs.forEach(log => {
    const day = new Date(log.createdAt).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    if (day !== current) {
      current = day;
      groups.push({ day, logs: [] });
    }
    groups[groups.length - 1].logs.push(log);
  });
  return groups;
}

const ACTION_FILTERS = [
  { label: 'All',     value: '' },
  { label: 'Created', value: 'CREATE' },
  { label: 'Updated', value: 'UPDATE' },
  { label: 'Status',  value: 'STATUS' },
  { label: 'Deleted', value: 'DELETE' },
];

/* ─── Main panel ─────────────────────────────────────────────────────────── */
export default function GlobalHistoryPanel({ module, onClose }) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const [logs, setLogs]               = useState([]);
  const [total, setTotal]             = useState(0);
  const [pages, setPages]             = useState(1);
  const [page, setPage]               = useState(1);
  const [loading, setLoading]         = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError]             = useState(null);
  const [search, setSearch]           = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const scrollRef = useRef(null);

  const LIMIT = 20;

  const load = useCallback(async (pageNum = 1, append = false, fresh = false) => {
    if (pageNum === 1) setLoading(true); else setLoadingMore(true);
    setError(null);
    try {
      // An explicit refresh must bypass api.js's 30s GET cache — otherwise the
      // feed re-requests the same key and returns the cached page, so the
      // refresh button appears to do nothing and new events never show up.
      if (fresh) bustCache('/activity-logs/feed');
      const { data } = await activityAPI.getFeed({ module, page: pageNum, limit: LIMIT });
      setTotal(data.total);
      setPages(data.pages);
      setPage(pageNum);
      setLogs(prev => append ? [...prev, ...data.logs] : data.logs);
    } catch {
      setError('Could not load activity feed.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [module]);

  useEffect(() => { load(1); }, [load]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const filtered = logs.filter(log => {
    const matchAction = actionFilter
      ? log.action.toUpperCase().includes(actionFilter)
      : true;
    const matchSearch = search.trim()
      ? (log.targetName || '').toLowerCase().includes(search.toLowerCase()) ||
        (log.user?.name  || '').toLowerCase().includes(search.toLowerCase())
      : true;
    return matchAction && matchSearch;
  });

  const grouped = groupByDate(filtered);

  // Module accent: indigo for Candidate, violet for Requirement.
  // On light mode we use the solid 600-level for readability of the small
  // badge numerals; on dark we keep the soft 300-level so it glows.
  const moduleColor = module === 'Candidate'
    ? {
        accent: '#6366f1',
        glow:   isLight ? 'rgba(99,102,241,0.15)' : 'rgba(99,102,241,0.4)',
        light:  isLight ? '#4338CA' : '#a5b4fc',
        icon: '👤',
      }
    : {
        accent: '#8b5cf6',
        glow:   isLight ? 'rgba(139,92,246,0.15)' : 'rgba(139,92,246,0.4)',
        light:  isLight ? '#6D28D9' : '#c4b5fd',
        icon: '📋',
      };

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed',
        right: 0,
        top: 0,
        width: '100%',
        maxWidth: 480,
        height: '100vh',
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--surface-flat)',
        borderLeft: `1px solid ${moduleColor.accent}33`,
        boxShadow: '-8px 0 32px rgba(0,0,0,0.15)',
        animation: 'slideInLeft 0.28s cubic-bezier(0.16,1,0.3,1)',
      }}
    >
        {/* ── Header ── */}
        <div
          className="flex items-center justify-between px-5 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-center gap-3 min-w-0">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
              style={{
                background: `linear-gradient(135deg, ${moduleColor.accent}30, ${moduleColor.accent}15)`,
                border: `1px solid ${moduleColor.accent}40`,
                boxShadow: `0 0 16px ${moduleColor.glow}`,
              }}
            >
              {moduleColor.icon}
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="font-bold text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                {module} History
              </h2>
              <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-tertiary)' }}>
                All changes across every {module.toLowerCase()}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {!loading && (
              <span
                className="text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap"
                style={{
                  background: `${moduleColor.accent}18`,
                  color: moduleColor.light,
                  border: `1px solid ${moduleColor.accent}30`,
                }}
              >
                {total.toLocaleString()} events
              </span>
            )}
            <button
              onClick={() => load(1, false, true)}
              title="Refresh"
              disabled={loading || loadingMore}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.background = 'var(--hover-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-tertiary)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: (loading || loadingMore) ? 'spin 0.7s linear infinite' : 'none' }}>
                <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
              </svg>
            </button>
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-lg flex items-center justify-center transition-all text-base"
              style={{ color: 'var(--text-tertiary)' }}
              onMouseEnter={e => {
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.background = 'var(--hover-bg)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.color = 'var(--text-tertiary)';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Filters ── */}
        <div
          className="px-4 py-3 space-y-2 flex-shrink-0"
          style={{ borderBottom: '1px solid var(--border-subtle)' }}
        >
          {/* Search */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2"
              width="13" height="13" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ color: 'var(--text-faint)' }}
            >
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={`Search by ${module.toLowerCase()} name or user…`}
              className="w-full pl-8 pr-3 py-2 text-xs rounded-lg focus:outline-none transition-all"
              style={{
                background: 'var(--input-bg)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-primary)',
              }}
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-sm"
                style={{ color: 'var(--text-faint)' }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Action type filter chips */}
          <div className="flex gap-1.5 flex-wrap">
            {ACTION_FILTERS.map(f => {
              const active = actionFilter === f.value;
              return (
                <button
                  key={f.value}
                  onClick={() => setActionFilter(f.value)}
                  className="text-[11px] font-medium px-2.5 py-1 rounded-full transition-all border"
                  style={active ? {
                    background: `${moduleColor.accent}25`,
                    color: moduleColor.light,
                    borderColor: `${moduleColor.accent}40`,
                  } : {
                    background: 'var(--surface-3)',
                    color: 'var(--text-tertiary)',
                    borderColor: 'var(--border-subtle)',
                  }}
                >
                  {f.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Timeline ── */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 custom-scroll">
          {loading ? (
            <div className="flex flex-col items-center justify-center h-52 gap-3">
              <div
                className="w-9 h-9 rounded-full border-2 border-t-transparent"
                style={{
                  borderColor: `${moduleColor.accent}40`,
                  borderTopColor: moduleColor.accent,
                  animation: 'spin 0.75s linear infinite',
                }}
              />
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                Loading activity feed…
              </p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-52 gap-3 text-center">
              <span className="text-3xl">⚠️</span>
              <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{error}</p>
              <button
                onClick={() => load(1)}
                className="text-sm font-medium transition-colors"
                style={{ color: moduleColor.light }}
              >
                Try again
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-52 gap-3 text-center">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-2xl"
                style={{
                  background: `${moduleColor.accent}10`,
                  border: `1px solid ${moduleColor.accent}20`,
                }}
              >
                {moduleColor.icon}
              </div>
              <div>
                <p className="font-medium text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {logs.length === 0 ? 'No activity yet' : 'No matches found'}
                </p>
                <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                  {logs.length === 0
                    ? `${module} changes will appear here as they happen`
                    : 'Try a different search or filter'}
                </p>
              </div>
              {(search || actionFilter) && (
                <button
                  onClick={() => { setSearch(''); setActionFilter(''); }}
                  className="text-xs font-medium transition-colors"
                  style={{ color: moduleColor.light }}
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              {grouped.map(({ day, logs: dayLogs }) => (
                <div key={day}>
                  {/* Date separator */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-px flex-1" style={{ background: 'var(--border-subtle)' }} />
                    <span
                      className="text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider"
                      style={{
                        background: 'var(--surface-3)',
                        color: 'var(--text-muted)',
                        border: '1px solid var(--border-subtle)',
                      }}
                    >
                      {day}
                    </span>
                    <div className="h-px flex-1" style={{ background: 'var(--border-subtle)' }} />
                  </div>

                  <div className="relative">
                    <div
                      className="absolute left-[4px] top-3 bottom-3 w-px"
                      style={{ background: `linear-gradient(to bottom, ${moduleColor.accent}40, transparent)` }}
                    />
                    <div className="space-y-2">
                      {dayLogs.map(log => <FeedEntry key={log.id} log={log} />)}
                    </div>
                  </div>
                </div>
              ))}

              {/* Load more */}
              {page < pages && (
                <div className="pt-2 pb-4 flex justify-center">
                  <button
                    onClick={() => load(page + 1, true)}
                    disabled={loadingMore}
                    className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl transition-all disabled:opacity-50"
                    style={{
                      background: `${moduleColor.accent}15`,
                      color: moduleColor.light,
                      border: `1px solid ${moduleColor.accent}30`,
                    }}
                  >
                    {loadingMore ? (
                      <>
                        <div
                          className="w-3.5 h-3.5 rounded-full border border-t-transparent animate-spin"
                          style={{ borderColor: `${moduleColor.accent}50`, borderTopColor: moduleColor.accent }}
                        />
                        Loading…
                      </>
                    ) : (
                      <>
                        ↓ Load more{' '}
                        <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>
                          ({total - logs.length} remaining)
                        </span>
                      </>
                    )}
                  </button>
                </div>
              )}

              {page >= pages && logs.length > 0 && (
                <p className="text-center text-xs pb-4" style={{ color: 'var(--text-muted)' }}>
                  All {total.toLocaleString()} events loaded
                </p>
              )}
            </div>
          )}
        </div>
    </div>,
    document.body
  );
}
